import { camelCase, startCase } from 'lodash';
import { captureError, ErrorContext } from './datadog-error';

// https://docs.datadoghq.com/logs/log_collection/javascript/?s=rea
// Re-export for backward compatibility

export { captureError, captureErrorInDatadog } from './datadog-error';
export type { ErrorContext } from './datadog-error';

/**
 * True when an API error is a 404 / "not found" — e.g. a Fern/Check error whose target resource no
 * longer exists (a stale ID). Checks both the HTTP status and Check's `{ error: { type: 'not_found' } }`
 * response body.
 */
export const isNotFoundError = (error: unknown): boolean => {
  const e = error as { statusCode?: number; body?: { error?: { type?: string } } };
  return e?.statusCode === 404 || e?.body?.error?.type === 'not_found';
};

/**
 * Authorization denials are sent as 401 too, but they come from a controller so they carry the
 * backend error envelope. Only a bare 401, from the auth filter, means the session itself is dead.
 */
export const isExpiredSession = (status: number | undefined, body: unknown): boolean => {
  if (status !== 401) return false;
  const hasEnvelope = (v: { displayMessage?: string; type?: string } | undefined) =>
    Boolean(v?.displayMessage || v?.type);
  const b = body as { displayMessage?: string; type?: string; error?: { displayMessage?: string; type?: string } };
  return !hasEnvelope(b) && !hasEnvelope(b?.error);
};

export const isExpiredSessionError = (error: unknown): boolean => {
  const e = error as {
    status?: number;
    statusCode?: number;
    status_code?: number;
    httpStatus?: number;
    response?: { status?: number; data?: unknown };
    body?: unknown;
  };
  const status = e?.statusCode ?? e?.status ?? e?.status_code ?? e?.httpStatus ?? e?.response?.status;
  // Fern puts the envelope on `body`, a raw axios error on `response.data`, and CustomError copies
  // it onto itself. Miss one and a denial on that path is mistaken for a dead session.
  return isExpiredSession(status, e?.body ?? e?.response?.data ?? e);
};

/**
 * True when a withdrawal was rejected up front by our own validation (monthly withdrawal limit,
 * blocked company, amount bounds) rather than failing part-way through a provider transfer. These
 * are 4xx, deterministic, and no money has moved, so the worker must be shown the reason instead
 * of the optimistic success screen we fall back to for flaky provider errors.
 */
export const isWithdrawalRejectedUpfront = (error: unknown): boolean => {
  const e = error as {
    httpStatus?: number;
    statusCode?: number;
    status?: number;
    response?: { status?: number };
  };
  const status = e?.httpStatus ?? e?.statusCode ?? e?.status ?? e?.response?.status;
  if (typeof status !== 'number' || status < 400 || status >= 500) return false;
  return status !== 408 && status !== 499 && status !== 401;
};

/**
 * True when an API error is a 403 / "forbidden" — the caller is authenticated but lacks
 * permission for the requested resource (e.g. a team they aren't scoped to).
 */
export const isForbiddenError = (error: unknown): boolean => {
  const e = error as { statusCode?: number };
  return e?.statusCode === 403;
};

/**
 * True when Check rejected a payroll `reopen` because the payroll is no longer `pending`
 * (Check error type `payroll_reopening_invalid`). Reopen is a one-way `pending -> draft`
 * transition, so a redundant call against an already-reopened payroll lands here. Such a payroll
 * is already in the desired editable `draft` state, so callers should treat this as success
 * rather than surfacing it as a failure.
 */
export const isPayrollAlreadyReopenedError = (error: unknown): boolean => {
  const e = error as { body?: { error?: { type?: string } } };
  return e?.body?.error?.type === 'payroll_reopening_invalid';
};

/**
 * Format error message for display to user.
 * Handles various error formats: Fern, Check, Bridge, 3rd party APIs.
 * Note: Pass error.body if the error is coming from fern
 */
export const formatErrorMessage = (error: any, defaultMessage: string): string => {
  const message = defaultMessage || 'Something went wrong. Please try again later.';

  const canErrorHaveEmbeddedMessage =
    error?.message?.includes('{') && error?.message?.includes('}') && error?.message?.includes(':') ? true : false;

  if (!canErrorHaveEmbeddedMessage && error?.displayMessage) {
    return error.displayMessage;
  }

  // The backend reserves displayMessage for curated user-facing copy (e.g. the payroll
  // credit-limit explanation) but falls back to a generic string when it has nothing
  // curated — in that case the raw message is the more informative one to show.
  const GENERIC_BACKEND_DISPLAY_MESSAGE = 'An unknown error has occurred. Please contact Thera!';
  if (error?.body?.displayMessage && error.body.displayMessage !== GENERIC_BACKEND_DISPLAY_MESSAGE) {
    return error.body.displayMessage;
  }

  if (error?.body?.message || error?.body?.displayMessage) {
    return error.body.message || error.body.displayMessage;
  }

  if (error?.body?.error) {
    return formatCheckErrorMessage(error);
  }

  if (error?.body?.detail) {
    return formatDetailErrorMessage(error);
  }

  if (error?.message) {
    if (error?.input_errors) {
      return error.message + '\n' + formatCheckErrorMessage(error);
    }

    if (!canErrorHaveEmbeddedMessage) {
      return error.message;
    }

    const errorMsg = parseEmbeddedJsonError(error);
    if (errorMsg) {
      return removeAnchorTags(errorMsg);
    }
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.message) {
    return error.response.message;
  }

  return message;
};

/**
 * Parse error message when it contains stringified JSON (3rd party APIs)
 */
const parseEmbeddedJsonError = (errorResponse: any): string | null => {
  const embeddedMessage = errorResponse?.message;
  if (!embeddedMessage) return null;

  try {
    const startIndex = embeddedMessage.indexOf('{');
    const endIndex = embeddedMessage.lastIndexOf('}') + 1;
    const embeddedJson = embeddedMessage.slice(startIndex, endIndex);
    const parsed = JSON.parse(embeddedJson);

    if (parsed?.error?.input_errors) {
      return formatCheckErrorMessage(parsed?.error);
    }

    if (parsed?.code === 'invalid_parameters') {
      return formatBridgeErrorMessage(parsed);
    }

    return (
      parsed?.errors?.[0]?.message ||
      parsed?.message ||
      parsed?.error?.message ||
      parsed?.error_message ||
      parsed?.error_description ||
      parsed?.error
    );
  } catch {
    return null;
  }
};

/**
 * Format Bridge API error messages
 */
const formatBridgeErrorMessage = (error: any): string | null => {
  try {
    if (error?.code === 'invalid_parameters') {
      const fieldKey = Object.keys(error.source?.key || {})[0];
      if (fieldKey) {
        const fieldName = startCase(camelCase(fieldKey.split('.')[1] || fieldKey));
        return `${fieldName}: ${error.source.key[fieldKey]}`;
      }
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Format Check API error messages
 */
const formatCheckErrorMessage = (error: any): string => {
  const errorsObject: { [fieldName: string]: string[] } = {};

  (error?.body?.error?.input_errors || error?.input_errors)?.forEach((inputError: any) => {
    if (errorsObject[inputError.field]) {
      errorsObject[inputError.field].push(inputError.message);
    } else {
      errorsObject[inputError.field] = [inputError.message];
    }
  });

  const errorArray = Object.entries(errorsObject).map(([field, messages]) => {
    return `${startCase(camelCase(field))}: ${messages.join(' ')}`;
  });

  if (!errorArray.length && error?.body?.error?.message) {
    errorArray.push(error.body.error.message);
  }

  return errorArray.join('\n');
};

/**
 * Format detail error messages (FastAPI validation errors)
 */
const formatDetailErrorMessage = (error: any): string => {
  const firstError = error?.body?.detail?.[0];
  if (!firstError) return '';

  return firstError.msg.replace('value', startCase(camelCase(firstError.loc[firstError.loc.length - 1])));
};

const removeAnchorTags = (str: string): string => {
  if (!str) return '';
  return str.replace(/<a href="[^"]*">[^<]*<\/a>/g, '');
};

/**
 * Note: Alias for backward compatibility with captureError
 */
export const logError = (error: Error, context?: ErrorContext): void => {
  captureError(error, context);
};

interface StytchRejection {
  error_type?: string;
  error_message?: string;
  request_id?: string;
  status_code?: number;
}

/**
 * Reports a failed Stytch call to Datadog.
 *
 * Two reasons this cannot just be `logError(error)`. The noise filter drops anything whose name
 * matches StytchApiError, so the raw rejection never reaches Error Tracking; wrapping it in a
 * plain Error gets it through. And Stytch's own activity log cannot attribute a failed
 * authenticate to a user, because an unresolved token has no user to attribute it to, so
 * `request_id` is the only key that joins a row in their dashboard to a session in ours.
 *
 * An `unknown_error` means Stytch never answered with an API error: a network failure, a failed
 * CAPTCHA retry, or a bug in our own `.then()`. The thrown name and message are the only clue then.
 */
export const logStytchError = (thrown: unknown, context: ErrorContext & { source: string }): void => {
  const rejection = (thrown ?? {}) as StytchRejection;
  const errorType = rejection.error_type || 'unknown_error';
  const thrownError = thrown instanceof Error ? thrown : undefined;

  logError(new Error(`Stytch ${context.source} failed: ${errorType}`), {
    category: 'auth',
    fingerprint: `stytch-${context.source}-${errorType}`,
    ...context,
    data: {
      ...context.data,
      errorType,
      requestId: rejection.request_id,
      statusCode: rejection.status_code,
      stytchMessage: rejection.error_message,
      thrownName: thrownError?.name,
      thrownMessage: thrownError?.message,
    },
  });
};
